// src/services/dashboardService.js
import apiClient from './apiClient';

class DashboardService {
    /**
     * Получить статистику дашборда
     */
    async getDashboardStats() {
        try {
            const response = await apiClient.get('/dashboard/stats');

            if (response.success) {
                return {
                    success: true,
                    data: response.data
                };
            }

            return {
                success: false,
                message: response.message || 'Ошибка получения статистики дашборда'
            };
        } catch (error) {
            console.error('Dashboard stats error:', error);
            return {
                success: false,
                message: error.message || 'Ошибка сети. Попробуйте позже.'
            };
        }
    }

    /**
     * Форматировать данные статистики для отображения
     */
    formatStatsForDisplay(statsData) {
        if (!statsData) return null;

        const { team_statistics, income_statistics, user_info } = statsData;

        return {
            teamStats: {
                directReferrals: team_statistics?.direct_referrals || {
                    total: 0,
                    active: 0,
                    inactive: 0,
                    new_this_week: 0,
                    new_this_month: 0
                },
                allTeam: team_statistics?.all_team || {
                    total: 0,
                    active: 0,
                    inactive: 0,
                    new_this_week: 0,
                    new_this_month: 0
                },
                levelDistribution: team_statistics?.level_distribution || {
                    'Бронзовый': 0,
                    'Серебряный': 0,
                    'Золотой': 0
                },
                depthDistribution: team_statistics?.depth_distribution || {},
                recentPartners: team_statistics?.recent_partners || [],
                growthTrends: team_statistics?.growth_trends || {
                    monthly: {},
                    weekly: {}
                }
            },
            incomeStats: {
                totalEarnedRub: income_statistics?.total_earned_rub || 0,
                totalEarnedUsd: income_statistics?.total_earned_usd || 0,
                currentMonthRub: income_statistics?.current_month_rub || 0,
                currentMonthUsd: income_statistics?.current_month_usd || 0,
                lastMonthRub: income_statistics?.last_month_rub || 0,
                lastMonthUsd: income_statistics?.last_month_usd || 0,
                growthPercentage: income_statistics?.growth_percentage || 0,
                balanceRub: income_statistics?.balance_rub || 0,
                balanceUsd: income_statistics?.balance_usd || 0
            },
            userInfo: {
                login: user_info?.login || '',
                level: user_info?.level || 'Бронзовый',
                status: user_info?.status || 'active',
                registrationDate: user_info?.registration_date || '',
                lastActivity: user_info?.last_activity || null
            }
        };
    }

    /**
     * Получить тренды роста команды
     */
    getGrowthTrends(statsData) {
        if (!statsData?.team_statistics?.growth_trends) {
            return {
                monthly: [],
                weekly: []
            };
        }

        const trends = statsData.team_statistics.growth_trends;

        // Форматируем месячные данные
        const monthlyData = Object.entries(trends.monthly || {}).map(([month, count]) => ({
            period: month,
            count: count,
            label: this.formatMonthLabel(month)
        }));

        // Форматируем недельные данные
        const weeklyData = Object.entries(trends.weekly || {}).map(([week, count]) => ({
            period: week,
            count: count,
            label: this.formatWeekLabel(week)
        }));

        return {
            monthly: monthlyData,
            weekly: weeklyData
        };
    }

    /**
     * Форматировать метку месяца
     */
    formatMonthLabel(monthString) {
        const [year, month] = monthString.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long'
        });
    }

    /**
     * Форматировать метку недели
     */
    formatWeekLabel(weekString) {
        const [year, week] = weekString.split('-W');
        return `${week} нед. ${year}`;
    }

    /**
     * Вычислить процент роста
     */
    calculateGrowthPercentage(current, previous) {
        if (previous === 0) {
            return current > 0 ? 100 : 0;
        }
        return Math.round(((current - previous) / previous) * 100);
    }

    /**
     * Получить статистику активности команды
     */
    getActivityStats(statsData) {
        if (!statsData?.team_statistics) {
            return {
                totalActive: 0,
                totalInactive: 0,
                activePercentage: 0,
                newThisWeek: 0,
                newThisMonth: 0
            };
        }

        const { all_team } = statsData.team_statistics;
        const total = all_team.total || 0;
        const active = all_team.active || 0;
        const inactive = all_team.inactive || 0;

        return {
            totalActive: active,
            totalInactive: inactive,
            activePercentage: total > 0 ? Math.round((active / total) * 100) : 0,
            newThisWeek: all_team.new_this_week || 0,
            newThisMonth: all_team.new_this_month || 0
        };
    }

    /**
     * Получить распределение по глубине структуры
     */
    getDepthDistribution(statsData) {
        if (!statsData?.team_statistics?.depth_distribution) {
            return [];
        }

        return Object.entries(statsData.team_statistics.depth_distribution)
            .map(([level, count]) => ({
                level: parseInt(level.replace('level_', '')),
                count: count,
                label: `${level.replace('level_', '')} уровень`
            }))
            .sort((a, b) => a.level - b.level);
    }

    /**
     * Получить топ-партнеров по активности
     */
    getTopPartners(recentPartners, limit = 5) {
        if (!Array.isArray(recentPartners)) {
            return [];
        }

        return recentPartners
            .filter(partner => partner.is_active)
            .slice(0, limit)
            .map(partner => ({
                ...partner,
                displayName: partner.full_name || partner.login,
                levelColor: this.getLevelColor(partner.level),
                timeAgo: this.getTimeAgo(partner.registration_date)
            }));
    }

    /**
     * Получить цвет для уровня партнера
     */
    getLevelColor(level) {
        const colors = {
            'Бронзовый': 'text-amber-600',
            'Серебряный': 'text-gray-500',
            'Золотой': 'text-yellow-500'
        };
        return colors[level] || 'text-gray-400';
    }

    /**
     * Вычислить время с момента регистрации
     */
    getTimeAgo(dateString) {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
            return 'вчера';
        } else if (diffDays < 7) {
            return `${diffDays} дней назад`;
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return `${weeks} ${weeks === 1 ? 'неделю' : 'недель'} назад`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return `${months} ${months === 1 ? 'месяц' : 'месяцев'} назад`;
        } else {
            const years = Math.floor(diffDays / 365);
            return `${years} ${years === 1 ? 'год' : 'лет'} назад`;
        }
    }

    /**
     * Подготовить данные для графиков
     */
    prepareChartData(statsData) {
        const trends = this.getGrowthTrends(statsData);

        return {
            monthlyGrowthChart: {
                labels: trends.monthly.map(item => item.label),
                data: trends.monthly.map(item => item.count),
                type: 'line'
            },
            weeklyGrowthChart: {
                labels: trends.weekly.map(item => item.label),
                data: trends.weekly.map(item => item.count),
                type: 'bar'
            },
            levelDistributionChart: {
                labels: ['Бронзовый', 'Серебряный', 'Золотой'],
                data: [
                    statsData?.team_statistics?.level_distribution?.['Бронзовый'] || 0,
                    statsData?.team_statistics?.level_distribution?.['Серебряный'] || 0,
                    statsData?.team_statistics?.level_distribution?.['Золотой'] || 0
                ],
                type: 'pie',
                colors: ['#D97706', '#6B7280', '#EAB308']
            }
        };
    }

    /**
     * Получить краткую сводку для отображения
     */
    getDashboardSummary(statsData) {
        if (!statsData) {
            return {
                totalTeam: 0,
                directReferrals: 0,
                activePercentage: 0,
                monthlyGrowth: 0,
                totalEarnings: 0,
                currentBalance: 0
            };
        }

        const { team_statistics, income_statistics } = statsData;

        return {
            totalTeam: team_statistics?.all_team?.total || 0,
            directReferrals: team_statistics?.direct_referrals?.total || 0,
            activePercentage: this.getActivityStats(statsData).activePercentage,
            monthlyGrowth: team_statistics?.all_team?.new_this_month || 0,
            totalEarnings: income_statistics?.total_earned_rub || 0,
            currentBalance: income_statistics?.balance_rub || 0
        };
    }
}

export const dashboardService = new DashboardService();
